import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { FilterVehicleType } from "@src/app/enum/filters-vehicle.enum";

import { isEqual } from "lodash";

import {
  ICheckboxValueFilters,
  IFilterVehicle,
  IFilterVehicleGroup,
  IFilterVehicleOption,
  ISubmitFilters,
} from "@src/app/interfaces/filters-vehicle.interface";
import { OverlayPanel, OverlayPanelModule } from "primeng/overlaypanel";
import { BehaviorSubject, Subscription, debounceTime } from "rxjs";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";
import { CommonModule } from "@angular/common";
import { PipesModule } from "@commons/pipes/pipes.module";
import { ButtonModule } from "primeng/button";
import { RadioButtonModule } from "primeng/radiobutton";
import { CheckboxModule } from "primeng/checkbox";
import { DirectivesModule } from "@commons/directives/directives.module";

const imports = [
  FormsModule,
  ReactiveFormsModule,
  OverlayPanelModule,
  TranslateModule,
  CommonModule,
  PipesModule,
  ButtonModule,
  RadioButtonModule,
  CheckboxModule,
  DirectivesModule,
];

@Component({
  selector: "app-filter-control",
  templateUrl: "./filter-control.component.html",
  styleUrls: ["./filter-control.component.scss"],
  standalone: true,
  imports,
})
export class FilterControlComponent implements OnInit, OnDestroy {
  public readonly FILTER_VEHICLE_TYPE = FilterVehicleType;

  @ViewChild('op') opPanel?: OverlayPanel;
  @ViewChild('listContainer') listContainer?: ElementRef<HTMLElement>;

  @Input("selectedControlValue")
  public set selectedControlValue(data: ISubmitFilters) {
    if (
      data &&
      !isEqual(data.selectedControlValue, this._selectedControlValue)
    ) {
      this._selectedControlValue = data.selectedControlValue;

      setTimeout(() => {
        const selected = this._flatOptions.find(
          (opt) => opt.value === this._selectedControlValue
        );

        this.selectedOptionLabel = selected?.label ?? null;
      });
    }
  }
  @Input("data")
  public set setData(data: IFilterVehicle) {
    this._data = data;

    this.isGroupedData = this.detectGroupedData(data);

    this._flatOptions = [];
    this._valueToIndexMap.clear();

    if (this.isGroupedData) {
      this._groupedOptionsRaw = (this._data.options as any[]) ?? [];
      this.groupedOptionsToRender = this._groupedOptionsRaw;

      this._groupExpanded.clear();
      this.initExpandedMap(this._groupedOptionsRaw, 0);

      const stack: any[] = [];
      for (let i = 0; i < this._groupedOptionsRaw.length; i++) stack.push(this._groupedOptionsRaw[i]);

      while (stack.length) {
        const node = stack.pop();

        if (this.isGroupNode(node)) {
          const ch = node.children ?? [];
          for (let i = ch.length - 1; i >= 0; i--) stack.push(ch[i]);
          continue;
        }

        if (node.value === undefined || node.value === null) {
          node.value = node.data?.Id ?? node.key; // bez regresu
        }

        this._flatOptions.push(node);
        this._valueToIndexMap.set(node.value, this._flatOptions.length - 1);
      }
    }
    else {
      (this._data.options as IFilterVehicleOption[]).forEach((opt, index) => {
        if (opt.value === undefined || opt.value === null) {
          opt.value = `opt_${index}`;
        }

        this._flatOptions.push(opt);
        this._valueToIndexMap.set(opt.value, index);
      });
    }

    this.setControls();
  }

  @Input() public optionsSelectedCount: number;
  @Input() public isVisibleSearchInput: boolean;
  @Input() public isDisabled: boolean;
  @Input() public maxSelectable?: number;

  @Output() public onSubmitFilters$: EventEmitter<ISubmitFilters> =
    new EventEmitter();

  public _selectedControlValue: any;
  private _selectedSingleModeControlData: any;

  public checkboxValues: ICheckboxValueFilters[] = [];
  public isLoading: boolean;
  public _data: IFilterVehicle;

  public searchInputValue$: BehaviorSubject<string> = new BehaviorSubject(
    null as any
  );
  public searchValue: string = "";
  public searchValuePipe: string = "";
  public selectedCheckboxCount: number = 0;
  public selectedOptionLabel: string | null;

  private ComponentSubsc: Subscription = new Subscription();

  private get isMulitpleSelectBox(): boolean {
    return this._data.type === FilterVehicleType.MultipleCheckbox;
  }

  public isGroupedData = false;
  private _flatOptions: IFilterVehicleOption[] = [];
  private _valueToIndexMap = new Map<any, number>();

  public groupedOptionsToRender: any[] = [];
  private _groupedOptionsRaw: any[] = [];
  private _groupExpanded = new Map<string, boolean>();
  private readonly _defaultExpandedDepth = 4;

  constructor(private changeDetRef: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.onChangeSearchInputValue();
  }

  ngOnDestroy(): void {
    this.ComponentSubsc.unsubscribe();
  }

  public onCheckboxChange(): void {
    this.selectedCheckboxCount = this.checkboxValues.filter(
      (item) => item.checkboxValue
    ).length;
  }

  public onSelectRadioButton(data: any): void {
    if (this._data.isVisibleSelectedOptionLabel) {
      this._selectedSingleModeControlData = data ? { ...data } : null;
    }

    if (!this.isMulitpleSelectBox) {
      setTimeout(() => {
        this.submit(this.opPanel!);
      });
    }
  }

  public submit(modal: OverlayPanel): void {
    if (this._selectedSingleModeControlData) {
      this.selectedOptionLabel = this._selectedSingleModeControlData.label;
    }

    this.onSubmitFilters$.emit({
      selectedControlValue: this.isMulitpleSelectBox
        ? this.checkboxValues
            .filter((x) => x.checkboxValue)
            .map((x) => x.optionValue)
        : this._selectedControlValue,
      checkboxValues: this.checkboxValues.filter((x) => x.checkboxValue),
    });

    modal?.hide();
  }

  public onShowDropdown(): void {
    document
      .getElementsByClassName("p-overlaypanel-content")
      .item(0)
      ?.classList.add("overlay-unset-padding");

    this.scrollToSelectedOnOpen();
  }

  public clearFilters(modal: OverlayPanel): void {
    this.clearFiltersValue();
    this.submit(modal);
  }

  public clearFiltersValue(): void {
    this.resetCustomLabel();
    this._selectedControlValue = null;
    this.clearCheckboxValues();
  }

  private resetCustomLabel(): void {
    this._selectedSingleModeControlData = null;
    this.selectedOptionLabel = null;
  }

  private onChangeSearchInputValue(): void {
    const sub = this.searchInputValue$
      .pipe(debounceTime(150))
      .subscribe((data) => {
        this.searchValuePipe = data;

        if (this.isGroupedData) {
          this.groupedOptionsToRender = this.filterTreeKeepGroups(
            this._groupedOptionsRaw,
            this.searchValuePipe
          );
        }

        this.changeDetRef.detectChanges();
      });
    this.ComponentSubsc.add(sub);
  }

  private setControls(): void {
    if (!this.isMulitpleSelectBox) return;

    this.checkboxValues = this._flatOptions.map((item) => ({
      optionValue: item.value,
      checkboxValue: false,
      data: item.data,
    }));
  }

  private clearCheckboxValues(): void {
    this.checkboxValues = this.checkboxValues.map((item) => ({
      ...item,
      checkboxValue: false,
    }));
    this.selectedCheckboxCount = 0;
  }

  private detectGroupedData(data: IFilterVehicle): boolean {
    const first = data?.options?.[0] as any;
    return !!first && Array.isArray(first.children);
  }

  public getIndexForValue(value: any): number {
    return this._valueToIndexMap.get(value)!;
  }

  public isGroupNode(node: any): boolean {
    return !!node && Array.isArray(node.children);
  }

  private filterTreeKeepGroups(nodes: any[], term: string): any[] {
    if (!nodes) return [];
    if (!term) return nodes;

    const q = term.toLowerCase();
    const result: any[] = [];

    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i];

      if (this.isGroupNode(node)) {
        const cloned: any = Object.assign({}, node);
        cloned.children = this.filterTreeKeepGroups(node.children, term);
        result.push(cloned);
        continue;
      }

      const label = (node.label ?? "").toString().toLowerCase();
      if (label.includes(q)) {
        result.push(node);
      }
    }

    return result;
  }

  private getGroupKey(node: any): string {
    return String(node?.key ?? node?.value ?? node?.label ?? "");
  }

  private initExpandedMap(nodes: any[] | undefined, level: number): void {
    if (!nodes) return;

    for (let i = 0; i < nodes.length; i++) {
      const n = nodes[i];
      if (!this.isGroupNode(n)) continue;

      const k = this.getGroupKey(n);
      this._groupExpanded.set(k, level < this._defaultExpandedDepth);

      this.initExpandedMap(n.children, level + 1);
    }
  }

  public isExpanded(node: any, level: number): boolean {
    if (this.searchValuePipe) return true;

    const k = this.getGroupKey(node);
    const v = this._groupExpanded.get(k);
    return v === undefined ? level < this._defaultExpandedDepth : v;
  }

  public toggleGroup(node: any, level: number, ev?: Event): void {
    ev?.stopPropagation();
    ev?.preventDefault();

    const k = this.getGroupKey(node);
    const current = this.isExpanded(node, level);
    this._groupExpanded.set(k, !current);
  }

  // scrollowanie do zaznaczonego elementu po ponownym otwarciu
  private getFirstSelectedValue(): any | null {
    if (this.isMulitpleSelectBox) {
      for (let i = 0; i < this.checkboxValues.length; i++) {
        if (this.checkboxValues[i].checkboxValue) return this.checkboxValues[i].optionValue;
      }
      return null;
    }

    return this._selectedControlValue ?? null;
  }

  private expandGroupsToValue(value: any): void {
    if (!this.isGroupedData) return;

    const visit = (nodes: any[] | undefined): boolean => {
      if (!nodes) return false;

      for (let i = 0; i < nodes.length; i++) {
        const n = nodes[i];

        if (this.isGroupNode(n)) {
          const foundInChildren = visit(n.children);
          if (foundInChildren) {
            const k = this.getGroupKey(n);
            this._groupExpanded.set(k, true);
            return true;
          }
          continue;
        }

        if (n?.value === value) return true;
        if (n?.data?.Id === value) return true;
        if (String(n?.key) === String(value)) return true;
      }

      return false;
    };

    visit(this._groupedOptionsRaw);
  }

  public scrollToSelectedOnOpen(): void {
    const selectedValue = this.getFirstSelectedValue();
    if (selectedValue === null || selectedValue === undefined) return;

    this.expandGroupsToValue(selectedValue);

    this.changeDetRef.detectChanges();

    setTimeout(() => {
      const idx = this._valueToIndexMap.get(selectedValue);
      if (idx === undefined || idx === null) return;

      const inputId = `${this._data.filterLabel}-${idx}`;

      const container = this.listContainer?.nativeElement;
      const el = container?.querySelector(`#${inputId}`) as HTMLElement | null;

      el?.scrollIntoView({ block: "center", behavior: "smooth" });
    });
  }

}
